import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet, apiPost } from '../services/api';

import type { PatientSearchItem } from '../components/Reception/QuickPatientSearch';
import NewPatientForm from '../components/Reception/NewPatientForm';
import OldVisitForm from '../components/Reception/OldVisitForm';
import PatientHistory from '../components/Reception/PatientHistory';
import ReceptionModuleHeader from '../components/Reception/ReceptionModuleHeader';
import AppShellLayout from '../components/layout/AppShellLayout';
import TodayQueueConsole from '../components/Reception/TodayQueueConsole';
import OnlineAppointmentsPanel, { type OnlineAppointmentRow } from '../components/Reception/OnlineAppointmentsPanel';
import CalendarView from '../components/Reception/CalendarView';
import VisitHistoryView from '../components/Reception/VisitHistoryView';
import NotificationCenter from '../components/common/NotificationCenter';
import MobileTaskView from '../components/common/MobileTaskView';
import PatientEditModal from '../components/careflow/PatientEditModal';
import DayTimeline from '../components/Reception/DayTimeline';
import { useI18n } from '../i18n/I18nProvider';
import { apiPatch } from '../services/api';
import { useToast } from '../components/common/ToastProvider';
import { subscribeCareflowSync } from '../services/careflowSync';

// NEW PREMIUM THEME
import '../components/Reception/ModernReception.css';

interface RecallRow {
  id: string | number;
  patient_id?: string | number;
  reason?: string;
  date?: string;
  [key: string]: unknown;
}

export default function Reception() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<'queue' | 'calendar' | 'timeline'>('queue');
  const [selectedPatient, setSelectedPatient] = useState<PatientSearchItem | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [activeForm, setActiveForm] = useState<'new' | 'old' | 'history'>('new');
  const [pendingRecallsCount, setPendingRecallsCount] = useState(0);
  const [pendingOnlineCount, setPendingOnlineCount] = useState(0);
  const [loadingRecalls, setLoadingRecalls] = useState(false);
  const [pendingRecalls, setPendingRecalls] = useState<RecallRow[]>([]);
  const [showRecallsPanel, setShowRecallsPanel] = useState(false);
  const [showOnlinePanel, setShowOnlinePanel] = useState(false);
  const [showNotificationCenter, setShowNotificationCenter] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPatient, setEditingPatient] = useState<any>(null);
  const { showToast } = useToast();

  const historyRef = useRef<HTMLDivElement | null>(null);

  const triggerRefresh = useCallback(() => {
    setRefreshKey(prev => prev + 1);
  }, []);

  const loadRecalls = useCallback(async () => {
    setLoadingRecalls(true);
    try {
      const res = await apiGet('/api/recalls/pending');
      const list = Array.isArray(res) ? (res as RecallRow[]) : [];
      setPendingRecalls(list);
      setPendingRecallsCount(list.length);
    } catch (e) {
      setPendingRecalls([]);
      setPendingRecallsCount(0);
    } finally {
      setLoadingRecalls(false);
    }
  }, []);

  // Separate effect with proper cleanup for abort support
  useEffect(() => {
    let cancelled = false;
    setLoadingRecalls(true);
    apiGet('/api/recalls/pending')
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res) ? (res as RecallRow[]) : [];
        setPendingRecalls(list);
        setPendingRecallsCount(list.length);
      })
      .catch(() => {
        if (!cancelled) {
          setPendingRecalls([]);
          setPendingRecallsCount(0);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingRecalls(false);
      });
    return () => { cancelled = true; };
  }, [refreshKey]);

  const loadOnlineAppointmentsCount = useCallback(async () => {
    // Light-weight count only: today -> next 14 days, statuses we care about.
    try {
      const today = new Date();
      const toIso = (d: Date) => {
        const x = new Date(d);
        x.setMinutes(x.getMinutes() - x.getTimezoneOffset());
        return x.toISOString().slice(0, 10);
      };
      const qs = new URLSearchParams();
      qs.set("date_from", toIso(today));
      qs.set("date_to", toIso(new Date(Date.now() + 14 * 86400000)));
      const data = (await apiGet(`/api/reception/online-appointments?${qs.toString()}`)) as OnlineAppointmentRow[];
      const list = Array.isArray(data) ? data : [];
      const pending = list.filter((r) => {
        const s = String(r.status || "").toUpperCase();
        return s === "BOOKED" || s === "CONFIRMED" || s === "RESCHEDULED";
      }).length;
      setPendingOnlineCount(pending);
    } catch {
      setPendingOnlineCount(0);
    }
  }, []);

  useEffect(() => {
    void loadOnlineAppointmentsCount();
  }, [loadOnlineAppointmentsCount, refreshKey]);

  useEffect(() => {
    const unsubscribe = subscribeCareflowSync(() => {
      triggerRefresh();
      void loadOnlineAppointmentsCount();
    });
    return () => unsubscribe();
  }, [triggerRefresh, loadOnlineAppointmentsCount]);

  const handleToggleRecallsPanel = () => {
    setShowRecallsPanel((prev) => !prev);
  };

  const handleToggleOnlinePanel = () => {
    setShowOnlinePanel((prev) => !prev);
  };

  const handleMarkRecallContacted = async (recallId: string | number) => {
    try {
      await apiPost(`/api/recalls/${recallId}/contacted`, {});
      await loadRecalls();
      await loadOnlineAppointmentsCount();
      triggerRefresh();
    } catch (e) {
      console.error('Failed to mark recall contacted', e);
    }
  };

  const handlePatientSelect = (patient: PatientSearchItem) => {
    setSelectedPatient(patient);
    if (historyRef.current && window.innerWidth < 1200) {
      historyRef.current.scrollIntoView({ behavior: 'smooth' });
    }
    navigate(`/patient/${encodeURIComponent(String(patient.patient_id))}`);
  };

  const handlePatientAdded = (newPatient?: unknown) => {
    triggerRefresh();
    const normalized = (newPatient as PatientSearchItem) ?? null;
    setSelectedPatient(normalized);
    if (normalized?.patient_id) {
      navigate(`/patient/${encodeURIComponent(String(normalized.patient_id))}`);
    }
  };

  const handleVisitAdded = () => {
    triggerRefresh();
  };

  const handlePatientDeleted = () => {
    triggerRefresh();
    setSelectedPatient(null);
  };

  const handleEditPatient = (patient: any) => {
    setEditingPatient(patient);
    setShowEditModal(true);
  };

  const handleSavePatient = async (data: any) => {
    if (!editingPatient?.patient_id) return;
    try {
      await apiPatch(`/api/patients/${encodeURIComponent(editingPatient.patient_id)}`, data);
      showToast({ message: "Patient updated successfully", type: "success" });
      triggerRefresh();
      if (selectedPatient?.patient_id === editingPatient.patient_id) {
        setSelectedPatient({ ...selectedPatient, ...data });
      }
    } catch (e) {
      showToast({ message: "Failed to update patient", type: "error" });
      throw e;
    }
  };

  return (
    <div className="reception-body fade-in ui-page">
      {/* Mobile-first task view — only renders on ≤640px */}
      <MobileTaskView
        role="reception"
        patient={selectedPatient ? { patient_id: selectedPatient.patient_id, name: selectedPatient.name } : null}
        recallsCount={pendingRecallsCount}
        onlineCount={pendingOnlineCount}
        onPatientSelect={(p) => handlePatientSelect({ patient_id: String(p.patient_id), phone: "", name: p.name })}
        onOpenNotifications={() => setShowNotificationCenter(true)}
        onOpenPatientSearch={() => setShowNotificationCenter(false)}
        onEndSession={() => setSelectedPatient(null)}
      />
      <AppShellLayout
        moduleId="reception"
        header={(
          <ReceptionModuleHeader
            onPatientSelect={handlePatientSelect}
            onEdit={handleEditPatient}
            pendingRecallsCount={pendingRecallsCount}
            onToggleRecalls={() => setShowNotificationCenter(true)}
            pendingOnlineAppointmentsCount={pendingOnlineCount}
            onToggleOnlineAppointments={handleToggleOnlinePanel}
          />
        )}
        left={(
          <div className="reception-left-panel">
            <div className="view-mode-toggle glass-panel">
              <button
                className={`toggle-btn ${viewMode === 'queue' ? 'active' : ''}`}
                onClick={() => setViewMode('queue')}
              >
                {t('reception_view_queue')}
              </button>
              <button
                className={`toggle-btn ${viewMode === 'calendar' ? 'active' : ''}`}
                onClick={() => setViewMode('calendar')}
              >
                {t('reception_view_calendar')}
              </button>
              <button
                className={`toggle-btn ${viewMode === 'timeline' ? 'active' : ''}`}
                onClick={() => setViewMode('timeline')}
              >
                📊 Timeline
              </button>
            </div>

            {viewMode === 'queue' ? (
              <TodayQueueConsole
                refreshKey={refreshKey}
                onSelectPatient={(p) =>
                  handlePatientSelect({
                    patient_id: p.patient_id,
                    phone: (p as any).phone || "",
                    name: p.name,
                  })
                }
                onEdit={handleEditPatient}
              />
            ) : viewMode === 'calendar' ? (
              <CalendarView
                refreshKey={refreshKey}
                onPatientSelect={(p) =>
                  handlePatientSelect({
                    patient_id: p.patient_id,
                    phone: p.phone || p.patient_id,
                    name: p.name,
                  })
                }
              />
            ) : (
              <DayTimeline />
            )}
          </div>
        )}
        leftCollapsible={false}
        main={(
          <div className="reception-main-panel">
            <div className="form-tabs">
              <button
                type="button"
                className={`tab-btn tab-new ${activeForm === 'new' ? 'active' : ''}`}
                onClick={() => setActiveForm('new')}
              >
                {t('reception_tab_new_patient')}
              </button>
              <button
                type="button"
                className={`tab-btn tab-old ${activeForm === 'old' ? 'active' : ''}`}
                onClick={() => setActiveForm('old')}
              >
                {t('reception_tab_backdate_visit')}
              </button>
              <button
                type="button"
                className={`tab-btn tab-history ${activeForm === 'history' ? 'active' : ''}`}
                onClick={() => setActiveForm('history')}
              >
                {t('reception_tab_visit_history')}
              </button>
            </div>

            <div className={`form-container glass-panel ${activeForm === 'history' ? 'history-mode' : ''}`}>
              {activeForm === 'new' ? (
                <div id="new-patient-form" className="form-section animate-slide">
                  <div className="section-header">
                    <h3>{t('reception_section_today_registration')}</h3>
                    <p>{t('reception_section_today_registration_sub')}</p>
                  </div>
                  <NewPatientForm onPatientAdded={handlePatientAdded} />
                </div>
              ) : activeForm === 'old' ? (
                <div id="old-visit-form" className="form-section animate-slide">
                  <div className="section-header">
                    <h3>{t('reception_section_old_record')}</h3>
                    <p>{t('reception_section_old_record_sub')}</p>
                  </div>
                  <OldVisitForm onVisitAdded={handleVisitAdded} />
                </div>
              ) : (
                <VisitHistoryView
                  refreshKey={refreshKey}
                  onPatientSelect={(p) =>
                    handlePatientSelect({
                      patient_id: p.patient_id,
                      phone: p.phone,
                      name: p.name,
                    })
                  }
                />
              )}
            </div>

            {/* Unified Notification Center — replaces separate recalls + online drawers */}
            <NotificationCenter
              open={showNotificationCenter}
              onClose={() => setShowNotificationCenter(false)}
              onRecallContacted={(id) => {
                void handleMarkRecallContacted(id);
              }}
              onOnlineChanged={() => {
                void loadOnlineAppointmentsCount();
                triggerRefresh();
              }}
            />

            {/* Keep legacy OnlineAppointmentsPanel for now (used from header direct toggle) */}
            <OnlineAppointmentsPanel
              open={showOnlinePanel}
              onClose={handleToggleOnlinePanel}
              onChanged={() => {
                void loadOnlineAppointmentsCount();
                triggerRefresh();
              }}
            />

            <footer className="app-footer reception-footer">
              <div className="reception-footer-row">
                <p>
                  &copy; 2026 Thaila Dental Clinic &bull;{" "}
                  <span className="version reception-footer-version">
                    v{process.env.REACT_APP_VERSION || "2.4.0"}
                  </span>
                </p>
                <div className="reception-footer-actions">
                  <button
                    type="button"
                    title="Open Waiting Room display (TV screen)"
                    onClick={() => window.open('/waitroom', '_blank')}
                    className="reception-footer-btn waitroom"
                  >
                    🖥️ Waiting Room
                  </button>
                  <button
                    type="button"
                    title="Open Patient Self-Booking Portal"
                    onClick={() => window.open('/portal', '_blank')}
                    className="reception-footer-btn portal"
                  >
                    🔗 Patient Portal
                  </button>
                </div>
              </div>
            </footer>
          </div>
        )}
        right={(
          <div
            className="reception-right-panel"
            id="patient-history-panel"
            ref={historyRef}
          >
            <PatientHistory
              selectedPatient={selectedPatient as { patient_id: string; name?: string;[k: string]: unknown } | null}
              onDeleteSuccess={handlePatientDeleted}
              onVisitAdded={handleVisitAdded}
              refreshKey={refreshKey}
            />
          </div>
        )}
      />

      {editingPatient && (
        <PatientEditModal
          patient={editingPatient}
          isOpen={showEditModal}
          onClose={() => { setShowEditModal(false); setEditingPatient(null); }}
          onSave={handleSavePatient}
        />
      )}
    </div>
  );
}
